export interface PenTrack {
  penTrackKey?: number;
}

export interface Address {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface LoanData {
  payment?: number;
  totalAmountFinanced: number;
  loanNumber: string;
  loanType: number;
  amountFinanced: number;
  loanDate: string;
  firstPaymentDate: string;
  secondPaymentDate?: string;
  interestRate: number;
  termInMonths: number;
  paymentsPerYear: number;
  calculateByTerm?: boolean;
  referralCode?: string;
  coverageDate?: string;
  deductions: number;
  otherFees: number;
  insName?: string;
  insPolicyNumber?: string;
  insAgent?: string;
  insAgentPhone?: string;
  insuranceCompany?: string;
  insurancePolicyNumber?: string;
  agentName?: string;
  agentPhone?: string;
}

export interface VehicleExceptionAndOption {
  optionFourWheelDrive: boolean;
  optionTurbo: boolean;
  optionDiesel: boolean;
  optionHybrid: boolean;
  optionTotalLoss: boolean;
  optionLifted: boolean;
  optionMobility: boolean;
  optionPowerSportTotalLoss: boolean;
  exceptionV10: boolean;
  exceptionOneTon: boolean;
  commercialSurcharge: boolean;
}

export interface Vehicle {
  year: number;
  vin: string;
  mockVin: string;
  odometer: number;
  isCommercial: boolean;
  hasLiftKitOrOversizeTires: boolean;
  isModified: boolean;
  make: string;
  model: string;
  style: string;
  makeCode: string;
  modelCode: string;
  styleCode: string;
  bodyAttributes: string;
  isSalvageTitle: boolean;
  collateralType: number;
  collateralCode: number;
  certifiedPreowned: boolean;
  secondOwner: boolean;
  purchaseDate: string | Date;
  isVehicleManuallySelected: boolean;
  isVehicleNotFound: boolean;
  vehicleExceptionAndOption: VehicleExceptionAndOption;
  purchasePrice?: number;
  retailPrice?: number;
  modifiedVehicle?: boolean;
}

export interface Mmp {
  eligMilesPerYear?: number;
  eligYearsOwnVehicle?: number;
  isExcluded: boolean;
  isSelected?: boolean;
  isDiscounted?: boolean;
}
export interface Cvp {
  isSelected: boolean;
}
export interface Gap {
  isSelected: boolean;
  isDiscounted: boolean;
}

export interface Pep {
  isSelected: boolean;
  isDiscounted: boolean;
}

export interface VehicleReturn {
  isSelected: boolean;
}

export interface Ppm {
  isSelected: boolean;
}

export interface KeyProtection {
  isSelected: boolean;
}

export interface Sd {
  isSelected: boolean;
}

export interface PaymentProtection {
  isSelected: boolean;
  combinationId?: string;
}

export interface IRateRequest {
  portalAccountId: number;
  losId?: string;
  clientApplicationId: string;
  penTrack?: PenTrack;
  address?: Address;
  loanData: LoanData;
  vehicle?: Vehicle;
  mmp?: Mmp;
  ppm?: Ppm;
  keyProtection?: KeyProtection;
  gap?: Gap;
  sd?: Sd;
  pep?: Pep;
  cvp?: Cvp;
  vehicleReturn?: VehicleReturn;
  paymentProtection?: PaymentProtection;
}

export interface IVehicleInformation {
  year: number;
  vin: string;
  mockVin: string;
  odometer: number;
  isCommercial: boolean;
  make: string;
  model: string;
  style: string;
  bodyAttributes: string;
  isSalvageTitle: boolean;
  collateralType: number;
  collateralCode: number;
  purchaseDate: string;
  purchasePrice: number;
  retailPrice: number;
  otherCollateral: string;
  state: string;
  type: number;
  validateVin: boolean;
}

export type IRateRequestPartial = Partial<IRateRequest>;

export interface IProductPyramid {
  level1: string[];
  level2: string[];
  level3: string[];
}

/**
 * To create and initialize the productPyramid.
 * @returns IProductPyramid - empty object
 */
export function getNewProductPyramid(): IProductPyramid {
  return {
    level1: [],
    level2: [],
    level3: []
  };
}

/**
 * This enum will be used to check which level a product belongs to.
 * This enum will need to be updated when new products are added.
 */
export enum eProductLevel {
  mmp = 'level1',
  ppm = 'level1',
  keyProtection = 'level1',
  vehicleReturn = 'level1',
  tireWheel = 'level1',
  paintlessDent = 'level1',
  rsa = 'level1',
  lpt = 'level1',
  cpi = 'level1',
  clcd = 'level1',
  ersa = 'level1',
  powerSports = 'level1',
  gap = 'level2',
  pep = 'level1',
  ppi = 'level3',
  sd = 'level1',
  paymentProtection = 'level3',
  cvp = 'level1'
}

export type LevelNumber = 1 | 2 | 3;

export function getNewMmpRequest(): Mmp {
  return {
    eligMilesPerYear: null,
    eligYearsOwnVehicle: null,
    isExcluded: null,
    isSelected: null,
    isDiscounted: null
  };
}

export function getDefaultProdSelectionObject(): { isSelected: boolean } {
  return {
    isSelected: null
  };
}

export function getNewParentRateRequest(): IRateRequest {
  return {
    portalAccountId: null,
    losId: null,
    clientApplicationId: null,
    penTrack: {
      penTrackKey: null
    },
    address: {
      address1: null,
      address2: null,
      city: null,
      state: null,
      zipCode: null
    },
    loanData: {
      payment: null,
      totalAmountFinanced: null,
      loanNumber: null,
      loanType: null,
      amountFinanced: null,
      loanDate: null,
      firstPaymentDate: null,
      secondPaymentDate: null,
      interestRate: null,
      termInMonths: null,
      paymentsPerYear: null,
      calculateByTerm: null,
      referralCode: null,
      coverageDate: null,
      deductions: null,
      otherFees: null,
      insName: null,
      insPolicyNumber: null,
      insAgent: null,
      insAgentPhone: null
    },
    vehicle: {
      year: null,
      vin: null,
      mockVin: null,
      odometer: null,
      isCommercial: null,
      hasLiftKitOrOversizeTires: null,
      isModified: null,
      make: null,
      model: null,
      style: null,
      makeCode: null,
      modelCode: null,
      styleCode: null,
      bodyAttributes: null,
      isSalvageTitle: null,
      collateralType: null,
      collateralCode: null,
      certifiedPreowned: null,
      secondOwner: null,
      purchaseDate: null,
      isVehicleManuallySelected: null,
      isVehicleNotFound: null,
      retailPrice: 0,
      vehicleExceptionAndOption: {
        optionFourWheelDrive: false,
        optionTurbo: false,
        optionDiesel: false,
        optionHybrid: false,
        optionTotalLoss: false,
        optionLifted: false,
        optionMobility: false,
        optionPowerSportTotalLoss: false,
        exceptionV10: false,
        exceptionOneTon: false,
        commercialSurcharge: false
      }
    }
  };
}
